/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import gsap from 'gsap'
import { useActor } from '@xstate/react'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useRef } from 'react'
import { rpMediaQueries, theme } from '../../core/theme'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import { ChipOnBAB } from '../BettingBox/ChipOnBAB'
import { BgMixin } from '../BettingBox/styles'
import { Typography } from '../Typography'

const Container = styled.div`
  height: 100%;
  padding: 2px 0;
`

const Chip = styled(ChipOnBAB)`
  position: static;
  width: auto;
  height: auto;
  will-change: transform;
`

const ProgressBar = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px 0 0 5px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ fillPercentage: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${(props) => props.fillPercentage}%;
  background: var(--toastify-color-progress-success);
  transition: height 0.2s ease-out;
  box-shadow: 0 0 8px var(--toastify-color-progress-success);
`

const Inside = styled.div`
  ${BgMixin};
  border-radius: 5px 0 0 5px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 2px 5px;
  position: relative;
  user-select: none;

  ${rpMediaQueries.tablet} {
    padding: 5px 10px;
  }
`

const StyledButton = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: linear-gradient(
    135deg,
    rgba(180, 180, 180, 0.5),
    rgba(140, 140, 140, 0.5)
  );
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  will-change: transform;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle at 30% 30%,
      rgba(255, 255, 255, 0.3),
      transparent 60%
    );
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 50%;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: linear-gradient(
      135deg,
      rgba(200, 200, 200, 0.6),
      rgba(160, 160, 160, 0.6)
    );
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: scale(0.9);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.rpTheme.colors.white};
    font-size: 15px;
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    line-height: 1;
    position: relative;
    z-index: 1;
  }

  ${rpMediaQueries.tablet} {
    width: 30px;
    height: 30px;

    & > span {
      font-size: 20px;
    }
  }

  ${rpMediaQueries.tablet} {
    width: 40px;
    height: 40px;

    & > span {
      font-size: 20px;
    }
  }

  @keyframes subtlePulse {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 3px 12px rgba(255, 255, 255, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }
  }

  animation: subtlePulse 2s ease-in-out infinite;
`

const DecisionInsurance: FC<{
  box: BoxStore
}> = observer(({ box }) => {
  const { insurance, ui } = box
  const [state] = useActor(ui.service)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const chipRef = useRef<HTMLDivElement>(null)
  const incrementBtnRef = useRef<HTMLButtonElement>(null)
  const decrementBtnRef = useRef<HTMLButtonElement>(null)
  const isShakingRef = useRef(false)
  const isProcessingRef = useRef(false)

  useEffect(() => {
    if (!state.matches('insurance')) return
    ui.patch({ insuranceTemp: insurance.min })
  }, [state.value, insurance.min, ui])

  const shakeChip = () => {
    if (chipRef.current && !isShakingRef.current) {
      isShakingRef.current = true
      gsap.to(chipRef.current, {
        x: 5,
        duration: 0.08,
        repeat: 5,
        yoyo: true,
        ease: 'power1.inOut',
        onComplete: () => {
          gsap.set(chipRef.current, { x: 0 })
          isShakingRef.current = false
        },
      })
    }
  }

  const animateButtonPress = (
    buttonRef: React.RefObject<HTMLButtonElement>
  ) => {
    if (buttonRef.current) {
      // Начальное нажатие
      gsap.to(buttonRef.current, {
        scale: 0.85,
        duration: 0.1,
        ease: 'power2.out',
      })

      // Пульсация при зажатии (начнется через 400ms, когда начнется автоповтор)
      gsap.to(buttonRef.current, {
        scale: 0.9,
        duration: 0.3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 0.4,
      })
    }
  }

  const animateButtonRelease = (
    buttonRef: React.RefObject<HTMLButtonElement>
  ) => {
    if (buttonRef.current) {
      // Останавливаем все анимации на этой кнопке
      gsap.killTweensOf(buttonRef.current)

      gsap.to(buttonRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'elastic.out(1, 0.5)',
      })
    }
  }

  const startIncrement = () => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    animateButtonPress(incrementBtnRef)

    const increment = () => {
      const currentValue = ui.insuranceTemp
      const newAmount = currentValue + insurance.min
      const reachedMax = newAmount >= insurance.max
      const wasAlreadyAtMax = currentValue >= insurance.max

      ui.patch({
        insuranceTemp: reachedMax ? insurance.max : newAmount,
      })

      // Анимация только если УЖЕ были на максимуме и пытаемся увеличить
      if (wasAlreadyAtMax && reachedMax) {
        shakeChip()
      }
    }

    increment() // Первое срабатывание сразу
    intervalRef.current = setTimeout(() => {
      intervalRef.current = setInterval(increment, 200)
    }, 400)
  }

  const startDecrement = () => {
    if (isProcessingRef.current) return
    isProcessingRef.current = true

    animateButtonPress(decrementBtnRef)

    const decrement = () => {
      const currentValue = ui.insuranceTemp
      const newAmount = currentValue - insurance.min
      const reachedMin = newAmount <= insurance.min
      const wasAlreadyAtMin = currentValue <= insurance.min

      ui.patch({
        insuranceTemp: reachedMin ? insurance.min : newAmount,
      })

      // Анимация только если УЖЕ были на минимуме и пытаемся уменьшить
      if (wasAlreadyAtMin && reachedMin) {
        shakeChip()
      }
    }

    decrement() // Первое срабатывание сразу
    intervalRef.current = setTimeout(() => {
      intervalRef.current = setInterval(decrement, 200)
    }, 400)
  }

  const stopChange = (buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current)
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    isProcessingRef.current = false
    animateButtonRelease(buttonRef)
  }

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current)
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  const fillPercentage =
    ((ui.insuranceTemp - insurance.min) / (insurance.max - insurance.min)) * 100

  return (
    <Container>
      <Inside>
        <ProgressBar>
          <ProgressFill fillPercentage={fillPercentage} />
        </ProgressBar>
        <Typography
          variant="text4"
          color="white"
          css={{
            fontWeight: 900,
            whiteSpace: 'nowrap',
          }}
          as="div">
          Max {insurance.max}
        </Typography>
        <StyledButton
          ref={incrementBtnRef}
          type="button"
          onMouseDown={(e) => {
            if (e.button === 0 && !isProcessingRef.current) {
              startIncrement()
            }
          }}
          onMouseUp={() => stopChange(incrementBtnRef)}
          onMouseLeave={() => stopChange(incrementBtnRef)}
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => {
            e.preventDefault()
            if (!isProcessingRef.current) {
              startIncrement()
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            stopChange(incrementBtnRef)
          }}>
          <span>+</span>
        </StyledButton>

        <div ref={chipRef}>
          <Chip amount={ui.insuranceTemp} />
        </div>

        <StyledButton
          ref={decrementBtnRef}
          type="button"
          onMouseDown={(e) => {
            if (e.button === 0 && !isProcessingRef.current) {
              startDecrement()
            }
          }}
          onMouseUp={() => stopChange(decrementBtnRef)}
          onMouseLeave={() => stopChange(decrementBtnRef)}
          onContextMenu={(e) => e.preventDefault()}
          onTouchStart={(e) => {
            e.preventDefault()
            if (!isProcessingRef.current) {
              startDecrement()
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            stopChange(decrementBtnRef)
          }}>
          <span>-</span>
        </StyledButton>
        <Typography
          variant="text4"
          color="white"
          css={{
            fontWeight: 900,
            whiteSpace: 'nowrap',
          }}
          as="div">
          Min {insurance.min}
        </Typography>
      </Inside>
    </Container>
  )
})

export default DecisionInsurance
